import type { ApplicationContext } from '@hc-frontend/core-context';
import { createErrorOperationResult } from '@hc-frontend/core-utils-business-operation';
import { isPhoenixZipCode } from '@hc-frontend/core-utils-validations';

import { UpdateProspectJourneyParameters } from '../../entities';
import type {
  UpdateProspectJourneyRepository,
  UpdateProspectJourneyResult,
} from '../../repositories';

export type UpdateProspectJourneyUseCase = (
  parameters: UpdateProspectJourneyParameters,
) => Promise<UpdateProspectJourneyResult>;

export type UpdateProspectJourneyFactoryParams = Pick<
  ApplicationContext,
  'log'
> & {
  updateProspectJourney: UpdateProspectJourneyRepository;
};

export function updateProspectJourneyFactory({
  updateProspectJourney,
  log,
}: UpdateProspectJourneyFactoryParams): UpdateProspectJourneyUseCase {
  return async (parameters) => {
    const parsedParameters =
      UpdateProspectJourneyParameters.safeParse(parameters);

    if (!parsedParameters.success) {
      log({
        type: 'error',
        context: 'server',
        message: 'failedToUpdateProspectJourney',
        data: {
          rootCause: parsedParameters.error,
        },
      });
      return createErrorOperationResult({
        errors: [
          {
            code: 'failedToUpdateProspectJourney',
            rootCause: parsedParameters.error,
          },
        ],
      });
    }

    let askForZipCode = true;
    let isPhoenix = false;

    if (parameters.location) {
      askForZipCode = false;
      isPhoenix = isPhoenixZipCode(parameters.location.zipCode);
    }

    const result = await updateProspectJourney({
      ...parameters,
      additionalQuestions: {
        ASK_FOR_ZIP_CODE: askForZipCode,
        IS_PHOENIX: isPhoenix,
      },
    });

    if (!result.success) {
      log({
        context: 'server',
        type: 'error',
        message: result.errors[0].code,
        data: {
          rootCause: result.errors[0].rootCause,
        },
      });
    }

    return result;
  };
}
